// ---------------------------------------------------------------------------
// Structure upgrades, timed in war rounds
// ---------------------------------------------------------------------------
//
// Game time is war rounds: player.roundsPlayed grows by one for every round
// the player plays in ANY war (concurrent wars all count; addRoundsPlayed).
// An upgrade started at roundsPlayed R finishes at R + the level's rounds, so
// nothing has to touch running upgrades as rounds pass: settleUpgrades
// applies the finished ones. A war fights with the levels its Hive had when
// it started, so an upgrade finishing mid-war never affects that war.
//
// Cost to reach level L (levels[L], at the structure's cost tier):
//   raw     rawCost × costTier, split between the recipe's two resources in
//           the recipe's ratio
//   bucks   bucksCost × costTier
//   refined refinedUnits of the structure's refinedInput, only while the
//           converter switch is on
// One upgrade per structure at a time, any number of structures at once, and
// never past the player's arena's level cap. Finish Now costs
// gemPricePerRoundRemaining gems per round left.

import { DEFAULT_PROGRESSION } from './config.js'
import { clonePlayer, applyBundle, canAfford, ownsStructure } from './player.js'

export function upgradeCost(name, toLevel, p = DEFAULT_PROGRESSION) {
    const def = p.structures[name]
    if (!def) throw new Error(`Unknown structure "${name}"`)
    const lvl = p.levels[toLevel]
    if (!lvl) throw new Error(`No level ${toLevel} in levels.csv`)
    const total = Math.round(lvl.rawCost * def.costTier)
    const [[a, amtA], [b, amtB]] = Object.entries(def.recipe)
    const rawA = Math.round(total * amtA / (amtA + amtB))
    const cost = { raw: { [a]: rawA, [b]: total - rawA }, bucks: Math.round(lvl.bucksCost * def.costTier) }
    if (p.inputs.convertersEnabled && def.refinedInput && lvl.refinedUnits > 0) cost.refined = { [def.refinedInput]: lvl.refinedUnits }
    return cost
}

// Why the player can't start upgrading `name` now, or null if they can.
export function upgradeBlocker(player, name, p = DEFAULT_PROGRESSION) {
    if (!ownsStructure(player, name)) return 'not owned'
    const st = player.structures[name]
    if (st.upgrade) return 'already upgrading'
    const toLevel = st.level + 1
    if (!p.levels[toLevel]) return 'at the top level'
    if (toLevel > p.arenas[player.arena].levelCap) return `arena ${player.arena} caps levels at ${p.arenas[player.arena].levelCap}`
    if (!canAfford(player, upgradeCost(name, toLevel, p))) return 'not enough resources'
    return null
}

export function startUpgrade(player, name, p = DEFAULT_PROGRESSION) {
    const why = upgradeBlocker(player, name, p)
    if (why) throw new Error(`Can't upgrade ${name}: ${why}`)
    const toLevel = player.structures[name].level + 1
    const out = applyBundle(player, upgradeCost(name, toLevel, p), -1)
    out.structures[name].upgrade = { toLevel, finishAtRound: out.roundsPlayed + p.levels[toLevel].rounds }
    return settleUpgrades(out)
}

// Applies every upgrade whose timer has run out.
export function settleUpgrades(player) {
    const done = Object.entries(player.structures).filter(([, st]) => st.upgrade && st.upgrade.finishAtRound <= player.roundsPlayed)
    if (!done.length) return player
    const out = clonePlayer(player)
    for (const [name] of done) {
        out.structures[name].level = out.structures[name].upgrade.toLevel
        out.structures[name].upgrade = null
    }
    return out
}

// The player played `n` war rounds (in any war).
export function addRoundsPlayed(player, n = 1) {
    const out = clonePlayer(player)
    out.roundsPlayed += n
    return settleUpgrades(out)
}

export function roundsRemaining(player, name) {
    const up = player.structures[name]?.upgrade
    return up ? Math.max(0, up.finishAtRound - player.roundsPlayed) : 0
}

export function finishNowPrice(player, name, p = DEFAULT_PROGRESSION) {
    return Math.ceil(p.inputs.gemPricePerRoundRemaining * roundsRemaining(player, name))
}

export function finishNow(player, name, p = DEFAULT_PROGRESSION) {
    if (!player.structures[name]?.upgrade) throw new Error(`${name} isn't upgrading`)
    const out = applyBundle(player, { gems: finishNowPrice(player, name, p) }, -1)
    out.structures[name].upgrade.finishAtRound = out.roundsPlayed
    return settleUpgrades(out)
}
